import { useCallback, useEffect, useRef, useState } from 'react'
import { LexicalComposer } from '@lexical/react/LexicalComposer'
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin'
import { ContentEditable } from '@lexical/react/LexicalContentEditable'
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin'
import { ListPlugin } from '@lexical/react/LexicalListPlugin'
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin'
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin'
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { HeadingNode, QuoteNode, $createQuoteNode } from '@lexical/rich-text'
import { ListNode, ListItemNode, INSERT_UNORDERED_LIST_COMMAND, INSERT_ORDERED_LIST_COMMAND } from '@lexical/list'
import { LinkNode, TOGGLE_LINK_COMMAND } from '@lexical/link'
import { $setBlocksType } from '@lexical/selection'
import { $createHeadingNode } from '@lexical/rich-text'
import {
  $getRoot,
  $getSelection,
  $isRangeSelection,
  $createParagraphNode,
  FORMAT_TEXT_COMMAND,
  UNDO_COMMAND,
  REDO_COMMAND,
  type EditorState,
  type ElementNode,
} from 'lexical'
import { Spinner } from './ui'

/** Debounce before autosaving, long enough that normal typing produces one save. */
const AUTOSAVE_MS = 800

const theme = {
  paragraph: 'lexical-paragraph',
  quote: 'lexical-quote',
  heading: { h1: 'lexical-h1', h2: 'lexical-h2' },
  list: { ul: 'lexical-ul', ol: 'lexical-ol', listitem: 'lexical-li' },
  link: 'lexical-link',
  text: {
    bold: 'lexical-bold',
    italic: 'lexical-italic',
    underline: 'lexical-underline',
    strikethrough: 'lexical-strikethrough',
    code: 'lexical-code',
  },
}

export type SaveState = 'idle' | 'saving' | 'saved' | 'error'

interface Props {
  initialState: unknown
  readOnly?: boolean
  placeholder?: string
  onSave: (lexicalJson: unknown, plainText: string) => Promise<void>
}

export function LexicalSection({ initialState, readOnly = false, placeholder, onSave }: Props) {
  const [saveState, setSaveState] = useState<SaveState>('idle')

  return (
    <LexicalComposer
      initialConfig={{
        namespace: 'ibibik',
        theme,
        editable: !readOnly,
        nodes: [HeadingNode, QuoteNode, ListNode, ListItemNode, LinkNode],
        // Lexical needs a serialized state string, or null for a fresh editor.
        editorState: initialState ? JSON.stringify(initialState) : null,
        onError: (error: Error) => {
          console.error('lexical', error)
        },
      }}
    >
      <div className="lexical-shell">
        {!readOnly && <Toolbar saveState={saveState} />}
        <div className="relative">
          <RichTextPlugin
            contentEditable={<ContentEditable className="lexical-content" />}
            placeholder={
              <div className="lexical-placeholder">{placeholder ?? 'Write your answer…'}</div>
            }
            ErrorBoundary={LexicalErrorBoundary}
          />
        </div>
      </div>
      <HistoryPlugin />
      <ListPlugin />
      <LinkPlugin />
      {!readOnly && <Autosave onSave={onSave} onStateChange={setSaveState} />}
    </LexicalComposer>
  )
}

/**
 * Persists the editor state after a pause in typing. Both halves are stored: the
 * JSON so the editor can be restored exactly, and the plain text so the server
 * can decide whether a required section is filled and put it in the summary mail.
 */
function Autosave({
  onSave,
  onStateChange,
}: {
  onSave: (json: unknown, plain: string) => Promise<void>
  onStateChange: (s: SaveState) => void
}) {
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined)
  const latest = useRef<{ json: unknown; plain: string } | null>(null)
  const first = useRef(true)

  useEffect(() => () => clearTimeout(timer.current), [])

  const handleChange = useCallback(
    (editorState: EditorState) => {
      // Lexical fires once while mounting the initial state; that is not an edit.
      if (first.current) {
        first.current = false
        return
      }

      const json = editorState.toJSON()
      const plain = editorState.read(() => $getRoot().getTextContent())
      latest.current = { json, plain }

      clearTimeout(timer.current)
      timer.current = setTimeout(async () => {
        const pending = latest.current
        if (!pending) return
        onStateChange('saving')
        try {
          await onSave(pending.json, pending.plain)
          onStateChange('saved')
        } catch {
          onStateChange('error')
        }
      }, AUTOSAVE_MS)
    },
    [onSave, onStateChange],
  )

  return <OnChangePlugin onChange={handleChange} ignoreSelectionChange />
}

function Toolbar({ saveState }: { saveState: SaveState }) {
  const [editor] = useLexicalComposerContext()

  const block = (make: () => ElementNode) => () =>
    editor.update(() => {
      const selection = $getSelection()
      if ($isRangeSelection(selection)) $setBlocksType(selection, make)
    })

  const link = () => {
    const url = window.prompt('Link URL')
    if (url === null) return
    editor.dispatchCommand(TOGGLE_LINK_COMMAND, url.trim() === '' ? null : url.trim())
  }

  return (
    <div className="flex flex-wrap items-center gap-1 border-b border-zinc-200 px-2 py-1.5 dark:border-zinc-700">
      <TB onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold')} title="Bold">
        <span className="font-bold">B</span>
      </TB>
      <TB onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic')} title="Italic">
        <span className="italic">I</span>
      </TB>
      <TB onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'underline')} title="Underline">
        <span className="underline">U</span>
      </TB>
      <TB onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'strikethrough')} title="Strikethrough">
        <span className="line-through">S</span>
      </TB>

      <Divider />

      <TB onClick={block(() => $createHeadingNode('h1'))} title="Heading 1">
        H1
      </TB>
      <TB onClick={block(() => $createHeadingNode('h2'))} title="Heading 2">
        H2
      </TB>
      <TB onClick={block(() => $createParagraphNode())} title="Paragraph">
        ¶
      </TB>
      <TB onClick={block(() => $createQuoteNode())} title="Quote">
        ❝
      </TB>

      <Divider />

      <TB onClick={() => editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined)} title="Bullet list">
        •—
      </TB>
      <TB onClick={() => editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined)} title="Numbered list">
        1.
      </TB>
      <TB onClick={link} title="Link">
        🔗
      </TB>

      <Divider />

      <TB onClick={() => editor.dispatchCommand(UNDO_COMMAND, undefined)} title="Undo">
        ↺
      </TB>
      <TB onClick={() => editor.dispatchCommand(REDO_COMMAND, undefined)} title="Redo">
        ↻
      </TB>

      <div className="ml-auto pr-1 text-xs text-zinc-400">
        {saveState === 'saving' && (
          <span className="inline-flex items-center gap-1">
            <Spinner className="size-3" /> Saving
          </span>
        )}
        {saveState === 'saved' && <span className="text-emerald-600 dark:text-emerald-400">Saved</span>}
        {saveState === 'error' && <span className="text-red-600 dark:text-red-400">Not saved</span>}
      </div>
    </div>
  )
}

function TB({ onClick, title, children }: { onClick: () => void; title: string; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      title={title}
      aria-label={title}
      className="size-8 rounded-lg text-sm text-zinc-600 hover:bg-zinc-100 dark:text-zinc-300 dark:hover:bg-zinc-800"
    >
      {children}
    </button>
  )
}

function Divider() {
  return <div className="mx-1 h-5 w-px bg-zinc-200 dark:bg-zinc-700" />
}
